import { Injectable, NgZone } from '@angular/core';
import { Subject } from 'rxjs';

/**
 * CROSS-TAB SESSION SYNC
 *
 * THE PROBLEM
 *   The session lives in `localStorage` (AUTH_STATUS / AUTH_TOKEN / USER_TYPE / USER_DATA), which is
 *   shared by every tab on the origin. Log out in one tab and the other three keep rendering a full,
 *   working-looking application — the header, the menu, the last data they fetched — on top of a
 *   session that no longer exists. Every click from that point is a 401 the user cannot explain, and
 *   on a shared machine the previous user's screen is still sitting there for the next person.
 *
 *   The same hole exists in the other direction: sign in as a DIFFERENT user in a second tab and the
 *   first tab is now showing user A's data while holding user B's token. Its next request is made as
 *   B, and whatever comes back is painted into A's screen.
 *
 * HOW IT IS DETECTED — the browser already tells us
 *   `window.onstorage` fires in **every other tab** on the origin when localStorage changes, and
 *   NOT in the tab that made the change. That is exactly the signal we want, and it needs no
 *   polling, no socket, and no server round-trip. (A `BroadcastChannel` would work too and would be
 *   a second mechanism to keep in step with the one that already exists — localStorage IS the
 *   session, so watching localStorage is watching the session.)
 *
 * WHAT IT KEYS ON — the USER, not the token
 *   `signature()` is `type:user_id`, not the token string. If the app ever re-issues a token for the
 *   same person, that is not a session change and must not throw a modal in the user's face. A
 *   different id (or no id at all) is.
 *
 * WHAT IT DOES NOT DO
 *   It does not decide what the user sees. It reports `signed_out` / `switched` and stops there —
 *   `AppComponent` owns the dialog, because only the shell knows whether this tab is even showing an
 *   authenticated screen. A tab parked on the login page must not be told it has been signed out.
 */

const AUTH_TOKEN  = 'AUTH_TOKEN';
const AUTH_STATUS = 'AUTH_STATUS';
const USER_TYPE   = 'USER_TYPE';
const USER_DATA   = 'USER_DATA';

/** A change to any of these is a change to the session. Anything else in localStorage is not. */
const WATCHED = [AUTH_STATUS, AUTH_TOKEN, USER_TYPE, USER_DATA];

export type SessionChange =
  | 'signed_out'   // the session is gone — someone logged out in another tab
  | 'switched';    // a DIFFERENT user is now signed in on this origin

@Injectable({ providedIn: 'root' })
export class SessionSyncService {

  private readonly _change$ = new Subject<SessionChange>();
  /** Fires at most once per real change. Subscribe in `AppComponent`. */
  readonly change$ = this._change$.asObservable();

  private started = false;

  /** The session this tab believes it is running under. Null = signed out. */
  private known: string | null = null;

  constructor(private zone: NgZone) {}

  /** Call once, from AppComponent. Idempotent. */
  start(): void {
    if (this.started) return;
    this.started = true;

    this.known = this.signature();

    // `storage` fires outside Angular's zone, so a naive `next()` would update the model without
    // triggering change detection and the dialog would never appear.
    window.addEventListener('storage', (e: StorageEvent) => {
      this.zone.run(() => this.onStorage(e));
    });
  }

  /**
   * Who is signed in RIGHT NOW, according to localStorage. Null if nobody, or if the session is in a
   * torn state — which it briefly is during a logout, because `removeUserDetails()` removes the four
   * keys one at a time and each removal raises its own event. Treating a half-removed session as
   * "signed out" is correct, and the guard in `onStorage` makes sure we only say it once.
   */
  private signature(): string | null {
    try {
      if (localStorage.getItem(AUTH_STATUS) !== '1') return null;
      if (!localStorage.getItem(AUTH_TOKEN))         return null;

      const raw = localStorage.getItem(USER_DATA);
      if (!raw) return null;

      const user = JSON.parse(raw);
      const id   = user?.id ?? user?.reg_id ?? null;
      if (id == null) return null;

      return `${localStorage.getItem(USER_TYPE) || '?'}:${id}`;
    } catch {
      // Unparseable USER_DATA is not a session.
      return null;
    }
  }

  private onStorage(e: StorageEvent): void {
    // `key === null` means localStorage.clear() — that hits the session too.
    if (e.key !== null && !WATCHED.includes(e.key)) return;

    const before = this.known;
    const after  = this.signature();
    this.known   = after;

    // This tab never had a session, so it has nothing to lose. (It also stops a logout in tab A
    // from firing a "you were signed out" event in tab B, which is sitting on the login page.)
    if (!before) return;

    // Same user still signed in — a token rewrite, a menu key, an unrelated write. Not a change.
    if (before === after) return;

    this._change$.next(after ? 'switched' : 'signed_out');
  }
}
